import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, useCallback, Suspense } from "react";
import ReactDOM from 'react-dom';
import { GoogleMap, Circle, useJsApiLoader } from "@react-google-maps/api";
import apiAgent from "../../../../app/api/apiAgent";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationCrosshairs, faTimes, faHand, faMapMarkerAlt, faStreetView, faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";

// Import utility functions
import {
  useToTitleCase,
  usePropertyTypeColor,
  useSingleUnitMarkerOptions,
  useMultiUnitMarkerStyle,
  useGoogleMapOptions,
  useSearchCircleOptions,
  createPropertyMarkers,
  createLegendItems,
  formatPropertyAddress,
  isPointInPolygon as utilsIsPointInPolygon,
  calculateOptimalCircleForPolygon,
  filterPropertiesInPolygon,
  calculateMinimumBoundingCircle
} from "./MapSearchComponents/utils";

// Import components directly instead of lazy loading
import LoadingOverlay from './MapSearchComponents/components/LoadingOverlay';
import SearchTabs from './MapSearchComponents/components/SearchTabs';
import MapLegend from './MapSearchComponents/components/MapLegend';
import SearchInfoWindow from './MapSearchComponents/components/SearchInfoWindow';
import PropertyMarker from './MapSearchComponents/components/PropertyMarker';
import ConfirmReportPurchaseModalPopup from './ConfirmReportPurchaseModalPopup';
import ReportOwnerDetails from './ReportOwnerDetails';

const libraries = ["places", "marker", "drawing", "geometry"];
// Make sure the API key is accessed directly from the environment variable
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
// Add this line to explicitly access the Map ID
const GOOGLE_MAPS_ID = import.meta.env.VITE_GOOGLE_MAPS_ID || "";

// Check if the API key is set
const isApiKeySet = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY.length > 0;

// Add this to the component props
interface MapSearchProps {
  onPropertiesFound: (properties: any[]) => void;
}

// Define the ref interface
export interface MapSearchRef {
  handlePropertyLocationClick: (property: any) => void;
  showPropertyInfoWindow: (property: any, position: { lat: number; lng: number }) => void;
  handlePropertyReportClick: (property: any) => void;
}

// Custom hook for managing map event listeners and cleanup
const useMapEventCleanup = () => {
  // Store references to event listeners for cleanup
  const eventListenersRef = useRef<{ element: any; type: string; handler: any; }[]>([]);
  
  // Function to safely add event listener with automatic cleanup
  const addSafeEventListener = useCallback((element: any, type: string, handler: any) => {
    if (!element) return;
    
    // Add the event listener
    element.addEventListener(type, handler);
    
    // Store reference for cleanup
    eventListenersRef.current.push({ element, type, handler });
  }, []);
  
  // Function to remove a specific event listener
  const removeSafeEventListener = useCallback((element: any, type: string, handler: any) => {
    if (!element) return;
    
    // Remove the event listener
    element.removeEventListener(type, handler);
    
    // Remove from reference list
    eventListenersRef.current = eventListenersRef.current.filter(
      listener => !(listener.element === element && listener.type === type && listener.handler === handler)
    );
  }, []);
  
  // Function to clean up all event listeners
  const cleanupAllEventListeners = useCallback(() => {
    eventListenersRef.current.forEach(({ element, type, handler }) => {
      if (element && typeof element.removeEventListener === 'function') {
        element.removeEventListener(type, handler);
      }
    });
    
    // Clear the list
    eventListenersRef.current = [];
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAllEventListeners();
    };
  }, [cleanupAllEventListeners]);
  
  return {
    addSafeEventListener,
    removeSafeEventListener,
    cleanupAllEventListeners
  };
};

// Custom hook for managing document event listeners with proper cleanup
const useDocumentEventListener = () => {
  // Store references to document event listeners for cleanup
  const documentListenersRef = useRef<{ type: string; handler: EventListener }[]>([]);
  
  // Function to safely add document event listener with automatic cleanup
  const addDocumentEventListener = useCallback((type: string, handler: EventListener) => {
    // Add the event listener
    document.addEventListener(type, handler);
    
    // Store reference for cleanup
    documentListenersRef.current.push({ type, handler });
  }, []);
  
  // Function to remove a specific document event listener
  const removeDocumentEventListener = useCallback((type: string, handler: EventListener) => {
    // Remove the event listener
    document.removeEventListener(type, handler);
    
    // Remove from reference list
    documentListenersRef.current = documentListenersRef.current.filter(
      listener => !(listener.type === type && listener.handler === handler)
    );
  }, []);
  
  // Function to clean up all document event listeners
  const cleanupAllDocumentEventListeners = useCallback(() => {
    documentListenersRef.current.forEach(({ type, handler }) => {
      document.removeEventListener(type, handler);
    });
    
    // Clear the list
    documentListenersRef.current = [];
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAllDocumentEventListeners();
    };
  }, [cleanupAllDocumentEventListeners]);
  
  return {
    addDocumentEventListener,
    removeDocumentEventListener,
    cleanupAllDocumentEventListeners
  };
};

// Custom hook for managing Google Maps event listeners
const useGoogleMapsEventListener = () => {
  // Store references to Google Maps event listeners for cleanup
  const mapsEventListenersRef = useRef<{ listener: google.maps.MapsEventListener }[]>([]);
  
  // Function to safely add Google Maps event listener with automatic cleanup
  const addMapsEventListener = useCallback(
    <T extends google.maps.MVCObject>(
      instance: T,
      eventName: string,
      handler: Function
    ): google.maps.MapsEventListener => {
      // Add the event listener
      const listener = google.maps.event.addListener(
        instance, 
        eventName, 
        handler as any
      );
      
      // Store reference for cleanup
      mapsEventListenersRef.current.push({ listener });
      
      return listener;
    }, 
  []);
  
  // Function to remove a specific Google Maps event listener
  const removeMapsEventListener = useCallback((listener: google.maps.MapsEventListener) => {
    if (!listener) return;
    
    // Remove the event listener
    google.maps.event.removeListener(listener);
    
    // Remove from reference list
    mapsEventListenersRef.current = mapsEventListenersRef.current.filter(
      item => item.listener !== listener
    );
  }, []);
  
  // Function to clean up all Google Maps event listeners
  const cleanupAllMapsEventListeners = useCallback(() => {
    mapsEventListenersRef.current.forEach(({ listener }) => {
      if (listener) {
        google.maps.event.removeListener(listener);
      }
    });
    
    // Clear the list
    mapsEventListenersRef.current = [];
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAllMapsEventListeners();
    };
  }, [cleanupAllMapsEventListeners]);
  
  return {
    addMapsEventListener,
    removeMapsEventListener,
    cleanupAllMapsEventListeners
  };
};

// Create a wrapper component to handle the API key check
const MapSearchWrapper: React.ForwardRefRenderFunction<MapSearchRef, MapSearchProps> = (props, ref) => {
  // Check if the API key is set
  if (!isApiKeySet) {
    return (
      <div className="map-search-container">
        <div className="map-search-loading-container">
          <div className="map-search-loading-error">
            <h3>Google Maps API Key Not Set</h3>
            <p>The Google Maps API key is not set. Please set the REACT_APP_GOOGLE_MAPS_API_KEY environment variable.</p>
            <p>Contact your administrator for assistance.</p>
          </div>
        </div>
      </div>
    );
  }

  // Render the MapSearch component with forwarded ref
  return <MapSearchComponent {...props} forwardedRef={ref} />;
};

// Add a style element with CSS to disable map interactions when a modal is open
const modalBlockingStyles = `
.map-container.modal-open {
  pointer-events: none !important;
}
`;

// Define these utility functions outside of the component to avoid circular references

// Function to calculate polygon center
const calculatePolygonCenter = (polygon: google.maps.Polygon) => {
  if (!polygon) return null;
  
  const bounds = new google.maps.LatLngBounds();
  polygon.getPaths().forEach(path => {
    path.forEach(latLng => bounds.extend(latLng));
  });
  
  const center = bounds.getCenter();
  return { lat: center.lat(), lng: center.lng() };
};

// Function to calculate polygon diameter
const calculatePolygonDiameter = (polygon: google.maps.Polygon) => {
  if (!polygon) return 1.0; // Default to 1km if no polygon
  
  // Find the furthest point from the center
  const bounds = new google.maps.LatLngBounds();
  polygon.getPaths().forEach(path => {
    path.forEach(latLng => bounds.extend(latLng));
  });
  
  const center = bounds.getCenter();
  let maxDistance = 0;
  
  // Check each vertex of the polygon to find the furthest one from center
  polygon.getPaths().forEach(path => {
    path.forEach(latLng => {
      const distance = google.maps.geometry.spherical.computeDistanceBetween(center, latLng) / 1000;
      maxDistance = Math.max(maxDistance, distance);
    });
  });
  
  // Use the furthest point as the radius to ensure the circle includes all points
  // No additional buffer needed since we're finding the exact maximum distance
  const searchRadius = maxDistance;
  
  // Ensure a minimum of 0.5km and maximum of 5km radius
  return Math.min(Math.max(searchRadius, 0.5), 5.0);
};

// Function to check if point is inside polygon
const isPointInPolygon = (point: google.maps.LatLng, polygon: google.maps.Polygon) => {
  if (!polygon || !point) return false;
  
  const bounds = new google.maps.LatLngBounds();
  polygon.getPaths().forEach(path => {
    path.forEach(latLng => bounds.extend(latLng));
  });
  
  if (!bounds.contains(point)) return false;
  
  let isInside = false;
  polygon.getPaths().forEach(path => {
    const vertices = path.getArray();
    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
      if (((vertices[i].lat() > point.lat()) !== (vertices[j].lat() > point.lat())) &&
          (point.lng() < (vertices[j].lng() - vertices[i].lng()) * (point.lat() - vertices[i].lat()) / 
          (vertices[j].lat() - vertices[i].lat()) + vertices[i].lng())) {
        isInside = !isInside;
      }
    }
  });
  return isInside;
};

// Main component implementation
const MapSearchComponent: React.FC<MapSearchProps & { forwardedRef: React.ForwardedRef<MapSearchRef> }> = ({ onPropertiesFound, forwardedRef }) => {
  // Use the useJsApiLoader hook instead of LoadScript component
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries as any,
  });

  // Add the map event cleanup hook
  const { addSafeEventListener, removeSafeEventListener, cleanupAllEventListeners } = useMapEventCleanup();
  
  // Add the document event listener hook
  const { 
    addDocumentEventListener, 
    removeDocumentEventListener, 
    cleanupAllDocumentEventListeners 
  } = useDocumentEventListener();

  // Add the Google Maps event listener hook
  const { 
    addMapsEventListener, 
    removeMapsEventListener, 
    cleanupAllMapsEventListeners 
  } = useGoogleMapsEventListener();

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [center, setCenter] = useState({ lat: -30.5595, lng: 22.9375 }); // Default center of South Africa
  const [zoom, setZoom] = useState(6);
  const [mapMode, setMapMode] = useState<'pan' | 'marker' | 'polygon'>('marker');
  const [searchLocation, setSearchLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [searchRadius, setSearchRadius] = useState<number | null>(null);
  const [showCircle, setShowCircle] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [propertyMarkers, setPropertyMarkers] = useState<any[]>([]);
  const [legendItems, setLegendItems] = useState<{ type: string; color: string }[]>([]);
  const [showSearchInfoWindow, setShowSearchInfoWindow] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Add new states for polygon drawing
  const [drawingManager, setDrawingManager] = useState<google.maps.drawing.DrawingManager | null>(null);
  const [polygon, setPolygon] = useState<google.maps.Polygon | null>(null);
  const [polygonPath, setPolygonPath] = useState<google.maps.LatLng[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Refs
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const propertyInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  // Get utility hooks
  const toTitleCase = useToTitleCase();
  const getPropertyTypeColor = usePropertyTypeColor();
  const getSingleUnitMarkerOptions = useSingleUnitMarkerOptions();
  const getMultiUnitMarkerStyle = useMultiUnitMarkerStyle();
  const getGoogleMapOptions = useGoogleMapOptions();
  const searchCircleOptions = useMemo(() => ({
    strokeColor: '#92ab1c',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: '#92ab1c',
    fillOpacity: 0, // Make the circle transparent (no fill) for testing
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    zIndex: 1
  }), []);

  // State for owner details
  const [showOwnerDetails, setShowOwnerDetails] = useState(false);
  const [ownerDetailsPropertyId, setOwnerDetailsPropertyId] = useState('');
  const [ownerDetailsPropertyType, setOwnerDetailsPropertyType] = useState('');
  const [ownerDetailsStreetAddress, setOwnerDetailsStreetAddress] = useState('');
  const [ownerDetailsData, setOwnerDetailsData] = useState<any>(null);
  const [selectedPropertyForOwnerDetails, setSelectedPropertyForOwnerDetails] = useState<any>(null);
  
  // State for property report
  const [selectedPropertyForReport, setSelectedPropertyForReport] = useState<any>(null);
  const [isProcessingPropertyReport, setIsProcessingPropertyReport] = useState(false);
  const [showPropertyReportConfirmModal, setShowPropertyReportConfirmModal] = useState(false);
  
  // State for credit balance and modals
  const [creditBalance, setCreditBalance] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessingOwnerDetails, setIsProcessingOwnerDetails] = useState(false);
  const [showPurchaseCreditsModal, setShowPurchaseCreditsModal] = useState(false);
  const [ownerDetailsCost, setOwnerDetailsCost] = useState(
    parseInt(import.meta.env.VITE_INSIGHTS_COSTS_OWNER_DETAILS || '5', 10)
  ); // Get owner details cost from env settings, default to 5
  const [propertyReportCost, setPropertyReportCost] = useState(
    parseInt(import.meta.env.VITE_INSIGHTS_COSTS_PROPERTY_REPORT || '15', 10)
  ); // Get property report cost from env settings, default to 15

  // Initialize info windows
  useEffect(() => {
    if (isLoaded && !infoWindowRef.current) {
      infoWindowRef.current = new google.maps.InfoWindow();
      propertyInfoWindowRef.current = new google.maps.InfoWindow();
    }
  }, [isLoaded]);

  // Create a ref to break the circular reference
  const markerClickHandlerRef = useRef<(property: any) => void>();

  // Use imperative handle to expose methods to parent component
  useImperativeHandle(forwardedRef, () => ({
    handlePropertyLocationClick: (property: any) => {
      if (markerClickHandlerRef.current) {
        markerClickHandlerRef.current(property);
      }
    },
    showPropertyInfoWindow: (property: any, position: { lat: number; lng: number }) => {
      showPropertyInfoWindow(property, position);
    },
    handlePropertyReportClick: (property: any) => {
      handlePropertyReportClick(property);
    }
  }));

  // Remove the handlePropertyMarkerClick function from here as we'll define it later

  // Handle map load
  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMap(map);
  }, []);

  // Function to show info window with radius options
  const showInfoWindow = useCallback((location: { lat: number; lng: number }) => {
    if (!map || !infoWindowRef.current) return;
    
    // Close any existing info windows
    if (propertyInfoWindowRef.current) {
      propertyInfoWindowRef.current.close();
    }
    
    // Set showSearchInfoWindow to true
    setShowSearchInfoWindow(true);
    
    // Remove existing marker if it exists
    if (markerRef.current) {
      // Clean up existing event listener if it exists
      if (markerRef.current.element) {
        removeSafeEventListener(markerRef.current.element, 'gmp-click', () => {});
      }
      markerRef.current.map = null;
    }
    
    // Create new advanced marker
    if (google.maps.marker) {
      // Create a pin element with the correct color
      const pinBackground = new google.maps.marker.PinElement({
        background: "#92ab1c",
        borderColor: "#5a6b0f",
        glyphColor: "#FFFFFF",
      });
      
      // Create the advanced marker
      markerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: location,
        map: map,
        content: pinBackground.element,
        title: "Search location"
      });
      
      // Make the marker clickable
      if (markerRef.current.element) {
        // Create a named handler function so we can properly clean it up later
        const markerClickHandler = () => {
          showInfoWindow(location);
        };
        
        // Use our safe event listener helper
        addSafeEventListener(markerRef.current.element, 'gmp-click', markerClickHandler);
      }
    }
  }, [map, searchRadius, showCircle, setShowSearchInfoWindow, addSafeEventListener, removeSafeEventListener]);

  // Update handleMapClick to improve polygon drawing
  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng && map) {
      // Handle click based on current mode
      if (mapMode === 'marker') {
        const newLocation = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        };
        
        setSearchLocation(newLocation);
        setShowCircle(false);
        setSearchRadius(null);
        
        // Show the info window with radius options
        showInfoWindow(newLocation);
      }
      // In polygon mode, let the drawing manager handle the click
      // Just ensure we're not doing anything that would interfere
      else if (mapMode === 'polygon') {
        // Don't do anything here - let the drawing manager handle it
      }
      // In pan mode, do nothing
    }
  }, [map, mapMode, showInfoWindow]);

  // Handle using current location
  const handleUseCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          // Update map center and zoom
          if (map) {
            map.panTo(location);
            map.setZoom(15);
          }
          
          // Set the search location and show info window
          setSearchLocation(location);
          setShowCircle(false);
          setSearchRadius(null);
          
          // Show the info window with radius options
          showInfoWindow(location);
        },
        (error) => {
          toast.error(`Error getting location: ${error.message}`);
        }
      );
    } else {
      toast.error("Geolocation is not supported by your browser");
    }
  }, [map, showInfoWindow]);

  // Center map on current location without showing search window
  const centerMapOnCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          // Update map center and zoom
          if (map) {
            map.panTo(location);
            map.setZoom(15);
          }
        },
        (error) => {
          console.error(`Error getting location: ${error.message}`);
          // Don't show error toast on initial load
        }
      );
    }
  }, [map]);

  // Use user's current location when map first loads
  useEffect(() => {
    if (isLoaded && map) {
      // Only try to get location on first load
      centerMapOnCurrentLocation();
    }
  }, [isLoaded, map, centerMapOnCurrentLocation]);

  // Function to search properties by radius
  const searchPropertiesByRadius = useCallback(async (location: { lat: number; lng: number }, radius: number): Promise<any[]> => {
    setIsSearching(true);
    
    // Clear existing markers and properties before starting a new search
    setPropertyMarkers([]);
    setLegendItems([]);
    
    try {
      const response = await apiAgent.PropertyReports.radiusSearch(location.lat, location.lng, radius);
      
      if (response && Array.isArray(response)) {
        // Check if more than 2000 properties found
        const propertyLimit = 2000;
        const exceedsLimit = response.length > propertyLimit;
        
        // If over the limit, limit the properties and show warning
        const limitedProperties = exceedsLimit ? response.slice(0, propertyLimit) : response;
        
        // Always call onPropertiesFound with the limited properties
        onPropertiesFound(limitedProperties);
        
        // Create markers for the properties
        if (limitedProperties.length > 0) {
          const markers = createPropertyMarkers(limitedProperties);
          setPropertyMarkers(markers);
          
          // Create legend items
          const items = createLegendItems(limitedProperties, getPropertyTypeColor);
          setLegendItems(items);
          
          // Show warning if limit exceeded
          if (exceedsLimit) {
            toast.warning("More than 2000 properties found, narrow your search to show all available properties in the area");
          }
        } else {
          // Show toast notification when no properties are found
          toast.info(`No properties found within ${radius}m radius`);
        }
        
        // Change to pan mode after search is completed
        setMapMode('pan');
        
        // Return the properties
        return limitedProperties;
      } else {
        toast.error("Invalid response from server");
        return [];
      }
    } catch (error) {
      toast.error("Error searching properties");
      return [];
    } finally {
      setIsSearching(false);
    }
  }, [onPropertiesFound, getPropertyTypeColor, setMapMode]);

  // Handle radius selection
  const handleRadiusSelect = useCallback((radius: number, location: { lat: number; lng: number }) => {
    setSearchRadius(radius);
    setShowCircle(true);
    
    // Close the info window
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
    
    // Set showSearchInfoWindow to false to prevent rendering the component
    setShowSearchInfoWindow(false);
    
    // Start the search
    searchPropertiesByRadius(location, radius);
  }, [searchPropertiesByRadius, setShowSearchInfoWindow]);

  // Function to handle owner details button click
  const handleOwnerDetailsClick = (property: any) => {
    // Set the selected property for owner details
    setSelectedPropertyForOwnerDetails(property);
    
    // Get the user's credit balance before showing the confirmation modal
    apiAgent.PropertyReports.getUserCreditBalance()
      .then(response => {
        // The API returns a number directly, not an object with creditBalance property
        const integerCreditBalance = Math.floor(response);
        setCreditBalance(integerCreditBalance);
        setShowConfirmModal(true);
      })
      .catch(error => {
        toast.error("Error fetching credit balance. Please try again.");
      });
  };

  // Function to handle property report button click
  const handlePropertyReportClick = (property: any) => {
    // Set the selected property for property report
    setSelectedPropertyForReport(property);
    
    // Get the user's credit balance before showing the confirmation modal
    apiAgent.PropertyReports.getUserCreditBalance()
      .then(response => {
        // The API returns a number directly, not an object with creditBalance property
        const integerCreditBalance = Math.floor(response);
        setCreditBalance(integerCreditBalance);
        setShowPropertyReportConfirmModal(true);
      })
      .catch(error => {
        toast.error("Error fetching credit balance. Please try again.");
      });
  };

  // Function to confirm owner details purchase
  const confirmOwnerDetailsPurchase = () => {
    if (!selectedPropertyForOwnerDetails) return;
    
    // Check if the user has enough credits
    if (creditBalance < ownerDetailsCost) {
      toast.error("Not enough credits to generate owner details.");
      setShowConfirmModal(false);
      return;
    }
    
    // Ensure propertyId is a valid string and not undefined or null
    const propertyId = selectedPropertyForOwnerDetails.propertyId ? 
      selectedPropertyForOwnerDetails.propertyId.toString() : '';
    
    // If propertyId is empty, show an error and return
    if (!propertyId) {
      toast.error("Property ID is missing. Cannot generate owner details.");
      return;
    }
    
    // Format the street address
    const streetAddress = formatPropertyAddress(selectedPropertyForOwnerDetails);
    const propertyType = selectedPropertyForOwnerDetails.propertyType || '';
    
    // Set processing state to true
    setIsProcessingOwnerDetails(true);
    
    // Generate owner details report
    apiAgent.PropertyReports.generateOwnerDetails(propertyId, streetAddress, propertyType)
      .then(response => {
        // Close the confirmation modal and reset processing state
        setShowConfirmModal(false);
        setIsProcessingOwnerDetails(false);
        
        if (!response) {
          toast.error("Failed to get owner details data. Please try again.");
          return;
        }
        
        try {
          // Store the response data first
          const ownerData = response;
          
          // Update all state variables at once
          setOwnerDetailsData(ownerData);
          setOwnerDetailsPropertyId(propertyId);
          setOwnerDetailsPropertyType(propertyType);
          setOwnerDetailsStreetAddress(streetAddress);
          
          // Then show the modal after a short delay to ensure state is updated
          setTimeout(() => {
            // Force using the data we just received rather than relying on state
            if (!ownerDetailsData && ownerData) {
              // Create a temporary modal directly with ReactDOM
              const tempModalDiv = document.createElement('div');
              tempModalDiv.className = 'owner-details-modal-container';
              document.body.appendChild(tempModalDiv);
              
              // First set the state
              setOwnerDetailsData(ownerData);
              
              // Then show the modal after ensuring data is available
              setShowOwnerDetails(true);
            } else {
              // Normal flow - just show the modal
              setShowOwnerDetails(true);
            }
            
            // Dispatch refresh event
            const refreshEvent = new CustomEvent('refreshPurchasedReports');
            document.dispatchEvent(refreshEvent);
          }, 200);
        } catch (error) {
          toast.error("Error processing owner details data. Please try again.");
        }
      })
      .catch(error => {
        // Reset processing state
        setIsProcessingOwnerDetails(false);
        
        // Close the confirmation modal
        setShowConfirmModal(false);
        
        toast.error(
          <div dangerouslySetInnerHTML={{ 
            __html: "Failed to generate owner details for this property, you were not charged.<br/><a href='https://help.entegral.net/en/articles/9890912-insights-overview#h_aefee2ab92' target='_blank' rel='noopener noreferrer' style='color: white; text-decoration: underline;'>Click here for more info</a>" 
          }} />
        );
      });
  };

  // Function to confirm property report purchase
  const confirmPropertyReportPurchase = () => {
    if (!selectedPropertyForReport) return;
    
    // Check if the user has enough credits
    if (creditBalance < propertyReportCost) {
      toast.error("Not enough credits to generate property report.");
      setShowPropertyReportConfirmModal(false);
      return;
    }
    
    // Ensure propertyId is a valid string and not undefined or null
    const propertyId = selectedPropertyForReport.propertyId ? selectedPropertyForReport.propertyId.toString() : '';
    
    // If propertyId is empty, show an error and return
    if (!propertyId) {
      toast.error("Property ID is missing. Cannot generate property report.");
      return;
    }
    
    // Format the street address using the utility function
    const streetAddress = formatPropertyAddress(selectedPropertyForReport);
    const propertyType = selectedPropertyForReport.propertyType || '';
    
    // Set processing state to true
    setIsProcessingPropertyReport(true);
    
    // Generate property report
    apiAgent.PropertyReports.generateFullPropertyReport(propertyId, streetAddress, propertyType)
      .then(response => {
        // Close the confirmation modal and reset processing state
        setShowPropertyReportConfirmModal(false);
        setIsProcessingPropertyReport(false);
        
        // Download the generated report
        if (response && response.reportId) {
          // Show processing message
          toast.info("Downloading property report...");
          
          apiAgent.PropertyReports.downloadReport(response.reportId)
            .then(downloadResponse => {
              // Create a blob URL and trigger download
              const url = window.URL.createObjectURL(downloadResponse.data);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `property-report-${propertyId}.pdf`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              
              toast.success("Property report downloaded successfully");
            })
            .catch(error => {
              console.error('Error downloading property report:', error);
              toast.error("Error downloading property report");
            });
        } else {
          toast.error("Failed to generate property report. No report ID returned.");
        }
      })
      .catch(error => {
        // Reset processing state
        setIsProcessingPropertyReport(false);
        
        // Close the confirmation modal
        setShowPropertyReportConfirmModal(false);
        
        toast.error(
          <div dangerouslySetInnerHTML={{ 
            __html: "Failed to generate property report for this property, you were not charged.<br/><a href='https://help.entegral.net/en/articles/9890912-insights-overview#h_aefee2ab92' target='_blank' rel='noopener noreferrer' style='color: white; text-decoration: underline;'>Click here for more info</a>" 
          }} />
        );
      });
  };

  // Show property info window
  const showPropertyInfoWindow = useCallback((property: any, position: { lat: number; lng: number }) => {
    if (!map || !property) return;
    
    // Skip this operation if any modal is open
    if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
      return;
    }
    
    // Create info window if it doesn't exist
    if (!propertyInfoWindowRef.current) {
      propertyInfoWindowRef.current = new google.maps.InfoWindow({
        maxWidth: 350,
        pixelOffset: new google.maps.Size(0, -30)
      });
    }
    
    // Close any existing info windows
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
    
    // Check if this is a multi-unit property
    const isMultiUnit = property.multiUnitProperties && property.multiUnitProperties.length > 1;
    
    // For multi-unit properties, ensure the property object is fully populated
    if (isMultiUnit && property.multiUnitProperties.length > 0) {
      console.log(`Showing info window for multi-unit property with ${property.multiUnitProperties.length} units`);
      
      // Create a div for the content
      const containerDiv = document.createElement('div');
      containerDiv.className = 'map-search-multi-unit-info-content';
      
      // Format property name/type for header
      const headerText = property.propertyName || property.propertyType;
      const formattedHeaderText = toTitleCase(headerText ? headerText.toLowerCase() : 'Multi-Unit Property');
      
      // Create the header
      const header = document.createElement('h6');
      header.className = 'map-search-multi-unit-info-header';
      header.textContent = formattedHeaderText;
      header.style.textTransform = 'none'; // Prevent CSS from overriding our formatting
      containerDiv.appendChild(header);
      
      // Create the unit selector dropdown
      const selectorContainer = document.createElement('div');
      selectorContainer.className = 'map-search-multi-unit-info-selector-container';
      selectorContainer.style.margin = '10px 0';
      
      const selectorLabel = document.createElement('label');
      selectorLabel.textContent = 'Unit: ';
      selectorLabel.className = 'map-search-multi-unit-info-selector-label';
      selectorContainer.appendChild(selectorLabel);
      
      const selector = document.createElement('select');
      selector.className = 'map-search-multi-unit-info-selector';
      selector.style.padding = '4px';
      selector.style.marginRight = '5px';
      
      // Sort the units by unit number (numerically if possible)
      const sortedUnits = [...property.multiUnitProperties].sort((a, b) => {
        // Try to convert unit numbers to integers for proper numerical sorting
        const aNum = a.unitNumber ? parseInt(a.unitNumber.toString().replace(/\D/g, '')) : NaN;
        const bNum = b.unitNumber ? parseInt(b.unitNumber.toString().replace(/\D/g, '')) : NaN;
        
        // If both are valid numbers, sort numerically
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }
        
        // Otherwise, fall back to string comparison
        const aStr = a.unitNumber ? a.unitNumber.toString() : '';
        const bStr = b.unitNumber ? b.unitNumber.toString() : '';
        return aStr.localeCompare(bStr);
      });
      
      // Add options for each unit
      sortedUnits.forEach((unit: any, index: number) => {
        const option = document.createElement('option');
        option.value = property.multiUnitProperties.indexOf(unit).toString(); // Use original index
        option.textContent = unit.unitNumber || `Unit ${index + 1}`;
        selector.appendChild(option);
      });
      
      selectorContainer.appendChild(selector);
      
      // Add unit count after the dropdown
      const unitCountLabel = document.createElement('span');
      unitCountLabel.textContent = ` / ${property.multiUnitProperties.length} units`;
      unitCountLabel.className = 'map-search-multi-unit-info-unit-count';
      selectorContainer.appendChild(unitCountLabel);
      
      containerDiv.appendChild(selectorContainer);
      
      // Create a container for the property details that will be updated
      const detailsContainer = document.createElement('div');
      detailsContainer.className = 'map-search-multi-unit-info-details';
      containerDiv.appendChild(detailsContainer);
      
      // Set the content and position
      propertyInfoWindowRef.current.setContent(containerDiv);
      propertyInfoWindowRef.current.setPosition(position);
      
      // Open the info window
      propertyInfoWindowRef.current.open(map);
      
      // Function to update the property details based on the selected unit
      const updatePropertyDetails = () => {
        // Find the selected unit selector
        const selector = document.querySelector('.gm-style-iw .map-search-multi-unit-info-selector') as HTMLSelectElement;
        if (!selector) return;
        
        const selectedIndex = parseInt(selector.value, 10);
        const selectedProperty = property.multiUnitProperties[selectedIndex];
        
        // Find the details container
        const detailsContainer = document.querySelector('.gm-style-iw .map-search-multi-unit-info-details');
        if (!detailsContainer) return;
        
        // Format ERF/Portion display for the selected unit
        let unitErfDisplay = selectedProperty.erf || '';
        if (selectedProperty.portion && selectedProperty.portion !== '0') {
          unitErfDisplay += `/${selectedProperty.portion}`;
        }
        
        // Get the property type
        const propertyType = selectedProperty.propertyType || 'Unknown Type';
        
        // Format address components in CamelCase
        const addressParts = [
          selectedProperty.streetNumber, 
          selectedProperty.streetName ? toTitleCase(selectedProperty.streetName) : '',
          selectedProperty.streetType ? toTitleCase(selectedProperty.streetType) : ''
        ].filter(Boolean);
        
        // Join address parts with spaces
        const address = addressParts.join(' ');
        
        // If street name is empty, use suburb as address
        const displayAddress = address || (selectedProperty.suburb ? toTitleCase(selectedProperty.suburb) : 'Address not available');
        
        // Format owner name in CamelCase
        const ownerName = selectedProperty.buyerName ? toTitleCase(selectedProperty.buyerName) : '';
        
        // Round the extent to 0 decimal places
        const unitRoundedExtent = selectedProperty.extent ? Math.round(selectedProperty.extent) : null;
        
        // Format dates for owner details and property report
        const formatDateString = (dateString: string | undefined): string => {
          if (!dateString) return '';
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return dateString;
          
          // Format as DD MMM YYYY (e.g., 12 Jun 2023)
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          });
        };
        
        // Check if owner details are available - only if they have been purchased (ownerDetailsGenerationDate exists)
        const hasOwnerDetails = selectedProperty.ownerDetails && selectedProperty.ownerDetails.length > 0 && selectedProperty.ownerDetailsGenerationDate;
        const ownerDetailsDate = formatDateString(selectedProperty.ownerDetailsGenerationDate);
        
        // Check if property report is available - must have both reportId and a valid reportGenerationDate
        const hasPropertyReport = selectedProperty.reportId && selectedProperty.reportId.length > 0 && selectedProperty.reportGenerationDate;
        const reportDate = formatDateString(selectedProperty.reportGenerationDate);
        
        // Create HTML for details with street view icon next to the heading
        detailsContainer.innerHTML = `
          ${unitErfDisplay ? `<p class="map-search-property-popup-detail"><strong>Erf:</strong> ${unitErfDisplay}</p>` : ''}
          <p class="map-search-property-popup-detail"><strong>Address:</strong> ${displayAddress}</p>
          <p class="map-search-property-popup-detail"><strong>Type:</strong> ${propertyType}</p>
          ${hasOwnerDetails ? `<p class="map-search-property-popup-detail"><strong>Owner:</strong> ${ownerName || 'n/a'}</p>` : ''}
          <p class="map-search-property-popup-detail"><strong>Size:</strong> ${unitRoundedExtent !== null ? `${unitRoundedExtent} m²` : 'n/a'}</p>
          
          <!-- Add owner details link if available -->
          ${hasOwnerDetails ? `
          <p class="map-search-property-popup-detail">
            <a href="javascript:void(0)" class="map-search-property-popup-link owner-details-link" title="Open owner details">
              <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="address-book" class="svg-inline--fa fa-address-book" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style="width: 14px; height: 14px; margin-right: 5px;">
                <path fill="currentColor" d="M96 0C60.7 0 32 28.7 32 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H96zM208 288h64c44.2 0 80 35.8 80 80c0 8.8-7.2 16-16 16H144c-8.8 0-16-7.2-16-16c0-44.2 35.8-80 80-80zm-32-96a64 64 0 1 1 128 0 64 64 0 1 1 -128 0zM512 80c0-8.8-7.2-16-16-16s-16 7.2-16 16v64c0 8.8 7.2 16 16 16s16-7.2 16-16V80zM496 192c-8.8 0-16 7.2-16 16v64c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm16 144c0-8.8-7.2-16-16-16s-16 7.2-16 16v64c0 8.8 7.2 16 16 16s16-7.2 16-16V336z"/>
              </svg>
              ${ownerDetailsDate} - Owner Details
            </a>
          </p>
          ` : ''}
          
          <!-- Add property report link if available -->
          ${hasPropertyReport ? `
          <p class="map-search-property-popup-detail">
            <a href="javascript:void(0)" class="map-search-property-popup-link property-report-link" title="Open property report">
              <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="file-lines" class="svg-inline--fa fa-file-lines" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" style="width: 14px; height: 14px; margin-right: 5px;">
                <path fill="currentColor" d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0zM112 256H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16z"/>
              </svg>
              ${reportDate} - Property Report
            </a>
          </p>
          ` : ''}
          
          <!-- Add action buttons container -->
          <div style="display: flex; gap: 10px; margin-top: 10px;">
            <button class="map-search-info-window-radius-btn property-report-btn" style="flex: 1; padding: 8px 0; border: 1px solid #92ab1c; border-radius: 4px; background: white; color: #333; cursor: pointer; font-weight: bold; transition: all 0.2s ease;">
              Property Report<br><small style="font-size: 11px; color: #555; font-weight: normal;">Buy: ${propertyReportCost} Credits</small>
            </button>
            <button class="map-search-info-window-radius-btn owner-details-btn" style="flex: 1; padding: 8px 0; border: 1px solid #92ab1c; border-radius: 4px; background: white; color: #333; cursor: pointer; font-weight: bold; transition: all 0.2s ease;">
              Owner Details<br><small style="font-size: 11px; color: #555; font-weight: normal;">Buy: ${ownerDetailsCost} Credits</small>
            </button>
          </div>
        `;
        
        // Add event listeners for the buttons using our safe event listeners
        // Property report button
        const propertyReportBtn = detailsContainer.querySelector('.property-report-btn');
        if (propertyReportBtn) {
          const reportBtnHandler = (e: Event) => {
            e.stopPropagation(); // Prevent the click from closing the info window
            
            // Skip if any modal is open
            if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
              return;
            }
            
            // Handle property report generation
            if (selectedProperty.reportId) {
              // If report already exists, download it
              apiAgent.PropertyReports.downloadReport(selectedProperty.reportId)
                .then(response => {
                  // Create a blob URL and trigger download
                  const url = window.URL.createObjectURL(response.data);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `property-report-${selectedProperty.propertyId}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                })
                .catch(error => {
                  console.error('Error downloading property report:', error);
                  toast.error("Error downloading property report");
                });
            } else {
              // Call handlePropertyReportClick directly instead of dispatching an event
              handlePropertyReportClick(selectedProperty);
            }
          };
          
          addSafeEventListener(propertyReportBtn, 'click', reportBtnHandler);
        }
        
        // Owner details button
        const ownerDetailsBtn = detailsContainer.querySelector('.owner-details-btn');
        if (ownerDetailsBtn) {
          const ownerDetailsBtnHandler = (e: Event) => {
            e.stopPropagation(); // Prevent the click from closing the info window
            
            // Skip if any modal is open
            if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
              return;
            }
            
            // Call the handleOwnerDetailsClick function with the property
            handleOwnerDetailsClick(selectedProperty);
          };
          
          addSafeEventListener(ownerDetailsBtn, 'click', ownerDetailsBtnHandler);
        }
        
        // Owner details link
        const ownerDetailsLink = detailsContainer.querySelector('.owner-details-link');
        if (ownerDetailsLink) {
          const ownerDetailsLinkHandler = (e: Event) => {
            e.stopPropagation(); // Prevent the click from closing the info window
            
            // Skip if any modal is open
            if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
              return;
            }
            
            // Check if owner details are available
            if (selectedProperty.ownerDetails && selectedProperty.ownerDetails.length > 0) {
              try {
                // Parse the owner details if it's a string
                let ownerDetailsData;
                if (typeof selectedProperty.ownerDetails === 'string') {
                  ownerDetailsData = JSON.parse(selectedProperty.ownerDetails);
                } else {
                  ownerDetailsData = selectedProperty.ownerDetails;
                }
                
                // Get the property ID, type, and address
                const propertyId = selectedProperty.propertyId ? selectedProperty.propertyId.toString() : '';
                const propertyType = selectedProperty.propertyType || '';
                
                // Format the street address
                let streetAddress = selectedProperty.streetAddress || '';
                if (!streetAddress) {
                  const addressParts = [
                    selectedProperty.streetNumber, 
                    selectedProperty.streetName,
                    selectedProperty.streetType
                  ].filter(Boolean);
                  
                  if (addressParts.length > 0) {
                    streetAddress = addressParts.join(' ');
                  } else {
                    streetAddress = selectedProperty.suburb || selectedProperty.province || '';
                  }
                }
                
                // Dispatch a custom event to be handled by the MapSearch component
                const event = new CustomEvent('showOwnerDetails', {
                  detail: {
                    propertyId,
                    propertyType,
                    streetAddress,
                    ownerDetailsData: {
                      ownerDetails: ownerDetailsData
                    }
                  }
                });
                document.dispatchEvent(event);
              } catch (error) {
                console.error('Error processing owner details:', error);
              }
            }
          };
          
          addSafeEventListener(ownerDetailsLink, 'click', ownerDetailsLinkHandler);
        }
        
        // Property report link
        const propertyReportLink = detailsContainer.querySelector('.property-report-link');
        if (propertyReportLink) {
          const propertyReportLinkHandler = (e: Event) => {
            e.stopPropagation(); // Prevent the click from closing the info window
            
            // Skip if any modal is open
            if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
              return;
            }
            
            // Download the property report if reportId is available
            if (selectedProperty.reportId) {
              // Show processing message
              const originalText = propertyReportLink.innerHTML;
              propertyReportLink.innerHTML = 'Processing...';
              (propertyReportLink as HTMLElement).style.pointerEvents = 'none';
              
              apiAgent.PropertyReports.downloadReport(selectedProperty.reportId)
                .then(response => {
                  // Create a blob URL and trigger download
                  const url = window.URL.createObjectURL(response.data);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `property-report-${selectedProperty.propertyId}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  
                  // Restore original text after download
                  setTimeout(() => {
                    propertyReportLink.innerHTML = originalText;
                    (propertyReportLink as HTMLElement).style.pointerEvents = 'auto';
                  }, 1000);
                })
                .catch(error => {
                  console.error('Error downloading property report:', error);
                  // Restore original text on error
                  propertyReportLink.innerHTML = originalText;
                  (propertyReportLink as HTMLElement).style.pointerEvents = 'auto';
                });
            } else {
              // Call handlePropertyReportClick directly instead of dispatching an event
              handlePropertyReportClick(selectedProperty);
            }
          };
          
          addSafeEventListener(propertyReportLink, 'click', propertyReportLinkHandler);
        }
      };
      
      // Add info window opened/closed event listeners with proper cleanup
      const infoWindowOpenListener = addMapsEventListener(
        propertyInfoWindowRef.current, 
        'domready', 
        () => {
          // Clean up existing listeners
          cleanupAllEventListeners();
          
          // Add event listener for the selector change with safe event listener
          const selector = document.querySelector('.gm-style-iw .map-search-multi-unit-info-selector') as HTMLSelectElement;
          if (selector) {
            const selectorChangeHandler = () => {
              updatePropertyDetails();
            };
            addSafeEventListener(selector, 'change', selectorChangeHandler);
            
            // Set a default value for the selector if not already set
            if (!selector.value && selector.options.length > 0) {
              selector.value = selector.options[0].value;
            }
          }
          
          // Initialize the details with the first unit - add a small delay to ensure DOM is ready
          setTimeout(() => {
            updatePropertyDetails();
          }, 50);
        }
      );
      
      // Clean up when info window closes
      const infoWindowCloseListener = addMapsEventListener(
        propertyInfoWindowRef.current, 
        'closeclick', 
        () => {
          removeMapsEventListener(infoWindowOpenListener);
          removeMapsEventListener(infoWindowCloseListener);
          cleanupAllEventListeners();
        }
      );
      
      return; // Exit early for multi-unit properties since we've set up everything
    }
    
    // If not a multi-unit property, continue with single unit handling
    // Format ERF/Portion display
    let erfDisplay = property.erf || '';
    if (property.portion && property.portion !== '0') {
      erfDisplay += `/${property.portion}`;
    }
    
    // Format address components in CamelCase
    const addressParts = [
      property.streetNumber, 
      property.streetName ? toTitleCase(property.streetName) : '',
      property.streetType ? toTitleCase(property.streetType) : ''
    ].filter(Boolean);
    
    // Join address parts with spaces
    const address = addressParts.join(' ');
    
    // If street name is empty, use suburb as address
    const displayAddress = address || (property.suburb ? toTitleCase(property.suburb) : 'Address not available');
    
    // Format owner name in CamelCase
    const ownerName = property.buyerName ? toTitleCase(property.buyerName) : '';
    
    // Round the extent to 0 decimal places
    const roundedExtent = property.extent ? Math.round(property.extent) : null;
    
    // Format dates for owner details and property report
    const formatDateString = (dateString: string | undefined): string => {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      // Format as DD MMM YYYY (e.g., 12 Jun 2023)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };
    
    // Check if owner details are available - only if they have been purchased (ownerDetailsGenerationDate exists)
    const hasOwnerDetails = property.ownerDetails && property.ownerDetails.length > 0 && property.ownerDetailsGenerationDate;
    const ownerDetailsDate = formatDateString(property.ownerDetailsGenerationDate);
    
    // Check if property report is available - must have both reportId and a valid reportGenerationDate
    const hasPropertyReport = property.reportId && property.reportId.length > 0 && property.reportGenerationDate;
    const reportDate = formatDateString(property.reportGenerationDate);
    
    // Create a div for the content
    const containerDiv = document.createElement('div');
    containerDiv.className = 'map-search-property-info-content';
    
    // Create HTML for details with street view icon next to the heading
    containerDiv.innerHTML = `
      ${erfDisplay ? `<p class="map-search-property-popup-detail"><strong>Erf:</strong> ${erfDisplay}</p>` : ''}
      <p class="map-search-property-popup-detail"><strong>Address:</strong> ${displayAddress}</p>
      <p class="map-search-property-popup-detail"><strong>Type:</strong> ${property.propertyType || 'Unknown Type'}</p>
      ${hasOwnerDetails ? `<p class="map-search-property-popup-detail"><strong>Owner:</strong> ${ownerName || 'n/a'}</p>` : ''}
      <p class="map-search-property-popup-detail"><strong>Size:</strong> ${roundedExtent !== null ? `${roundedExtent} m²` : 'n/a'}</p>
      
      <!-- Add owner details link if available -->
      ${hasOwnerDetails ? `
      <p class="map-search-property-popup-detail">
        <a href="javascript:void(0)" class="map-search-property-popup-link owner-details-link" title="Open owner details">
          <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="address-book" class="svg-inline--fa fa-address-book" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style="width: 14px; height: 14px; margin-right: 5px;">
            <path fill="currentColor" d="M96 0C60.7 0 32 28.7 32 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H96zM208 288h64c44.2 0 80 35.8 80 80c0 8.8-7.2 16-16 16H144c-8.8 0-16-7.2-16-16c0-44.2 35.8-80 80-80zm-32-96a64 64 0 1 1 128 0 64 64 0 1 1 -128 0zM512 80c0-8.8-7.2-16-16-16s-16 7.2-16 16v64c0 8.8 7.2 16 16 16s16-7.2 16-16V80zM496 192c-8.8 0-16 7.2-16 16v64c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm16 144c0-8.8-7.2-16-16-16s-16 7.2-16 16v64c0 8.8 7.2 16 16 16s16-7.2 16-16V336z"/>
          </svg>
          ${ownerDetailsDate} - Owner Details
        </a>
      </p>
      ` : ''}
      
      <!-- Add property report link if available -->
      ${hasPropertyReport ? `
      <p class="map-search-property-popup-detail">
        <a href="javascript:void(0)" class="map-search-property-popup-link property-report-link" title="Open property report">
          <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="file-lines" class="svg-inline--fa fa-file-lines" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" style="width: 14px; height: 14px; margin-right: 5px;">
            <path fill="currentColor" d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0zM112 256H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16z"/>
          </svg>
          ${reportDate} - Property Report
        </a>
      </p>
      ` : ''}
      
      <!-- Add action buttons container -->
      <div style="display: flex; gap: 10px; margin-top: 10px;">
        <button class="map-search-info-window-radius-btn property-report-btn" style="flex: 1; padding: 8px 0; border: 1px solid #92ab1c; border-radius: 4px; background: white; color: #333; cursor: pointer; font-weight: bold; transition: all 0.2s ease;">
          Property Report<br><small style="font-size: 11px; color: #555; font-weight: normal;">Buy: ${propertyReportCost} Credits</small>
        </button>
        <button class="map-search-info-window-radius-btn owner-details-btn" style="flex: 1; padding: 8px 0; border: 1px solid #92ab1c; border-radius: 4px; background: white; color: #333; cursor: pointer; font-weight: bold; transition: all 0.2s ease;">
          Owner Details<br><small style="font-size: 11px; color: #555; font-weight: normal;">Buy: ${ownerDetailsCost} Credits</small>
        </button>
      </div>
    `;
    
    // Set content and position
    propertyInfoWindowRef.current.setContent(containerDiv);
    propertyInfoWindowRef.current.setPosition(position);
    
    // Open the info window
    propertyInfoWindowRef.current.open(map);
    
    // Add info window opened/closed event listeners with proper cleanup
    const infoWindowOpenListener = addMapsEventListener(
      propertyInfoWindowRef.current, 
      'domready', 
      () => {
        // Clean up existing listeners
        cleanupAllEventListeners();
        
        // Add event listeners for the buttons using our safe event listeners
        // Property report button
        const propertyReportBtn = document.querySelector('.gm-style-iw .property-report-btn');
        if (propertyReportBtn) {
          const reportBtnHandler = (e: Event) => {
            e.stopPropagation(); // Prevent the click from closing the info window
            
            // Skip if any modal is open
            if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
              return;
            }
            
            // Handle property report generation
            if (property.reportId) {
              // If report already exists, download it
              apiAgent.PropertyReports.downloadReport(property.reportId)
                .then(response => {
                  // Create a blob URL and trigger download
                  const url = window.URL.createObjectURL(response.data);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `property-report-${property.propertyId}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                })
                .catch(error => {
                  console.error('Error downloading property report:', error);
                  toast.error("Error downloading property report");
                });
            } else {
              // Call handlePropertyReportClick directly instead of dispatching an event
              handlePropertyReportClick(property);
            }
          };
          
          addSafeEventListener(propertyReportBtn, 'click', reportBtnHandler);
        }
        
        // Owner details button
        const ownerDetailsBtn = document.querySelector('.gm-style-iw .owner-details-btn');
        if (ownerDetailsBtn) {
          const ownerDetailsBtnHandler = (e: Event) => {
            e.stopPropagation(); // Prevent the click from closing the info window
            
            // Skip if any modal is open
            if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
              return;
            }
            
            // Call the handleOwnerDetailsClick function with the property
            handleOwnerDetailsClick(property);
          };
          
          addSafeEventListener(ownerDetailsBtn, 'click', ownerDetailsBtnHandler);
        }
        
        // Owner details link
        const ownerDetailsLink = document.querySelector('.gm-style-iw .owner-details-link');
        if (ownerDetailsLink) {
          const ownerDetailsLinkHandler = (e: Event) => {
            e.stopPropagation(); // Prevent the click from closing the info window
            
            // Skip if any modal is open
            if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
              return;
            }
            
            // Check if owner details are available
            if (property.ownerDetails && property.ownerDetails.length > 0) {
              try {
                // Parse the owner details if it's a string
                let ownerDetailsData;
                if (typeof property.ownerDetails === 'string') {
                  ownerDetailsData = JSON.parse(property.ownerDetails);
                } else {
                  ownerDetailsData = property.ownerDetails;
                }
                
                // Get the property ID, type, and address
                const propertyId = property.propertyId ? property.propertyId.toString() : '';
                const propertyType = property.propertyType || '';
                
                // Format the street address
                let streetAddress = property.streetAddress || '';
                if (!streetAddress) {
                  const addressParts = [
                    property.streetNumber, 
                    property.streetName,
                    property.streetType
                  ].filter(Boolean);
                  
                  if (addressParts.length > 0) {
                    streetAddress = addressParts.join(' ');
                  } else {
                    streetAddress = property.suburb || property.province || '';
                  }
                }
                
                // Dispatch a custom event to be handled by the MapSearch component
                const event = new CustomEvent('showOwnerDetails', {
                  detail: {
                    propertyId,
                    propertyType,
                    streetAddress,
                    ownerDetailsData: {
                      ownerDetails: ownerDetailsData
                    }
                  }
                });
                document.dispatchEvent(event);
              } catch (error) {
                console.error('Error processing owner details:', error);
              }
            }
          };
          
          addSafeEventListener(ownerDetailsLink, 'click', ownerDetailsLinkHandler);
        }
        
        // Property report link
        const propertyReportLink = document.querySelector('.gm-style-iw .property-report-link');
        if (propertyReportLink) {
          const propertyReportLinkHandler = (e: Event) => {
            e.stopPropagation(); // Prevent the click from closing the info window
            
            // Skip if any modal is open
            if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
              return;
            }
            
            // Download the property report if reportId is available
            if (property.reportId) {
              // Show processing message
              const originalText = propertyReportLink.innerHTML;
              propertyReportLink.innerHTML = 'Processing...';
              (propertyReportLink as HTMLElement).style.pointerEvents = 'none';
              
              apiAgent.PropertyReports.downloadReport(property.reportId)
                .then(response => {
                  // Create a blob URL and trigger download
                  const url = window.URL.createObjectURL(response.data);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `property-report-${property.propertyId}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  
                  // Restore original text after download
                  setTimeout(() => {
                    propertyReportLink.innerHTML = originalText;
                    (propertyReportLink as HTMLElement).style.pointerEvents = 'auto';
                  }, 1000);
                })
                .catch(error => {
                  console.error('Error downloading property report:', error);
                  // Restore original text on error
                  propertyReportLink.innerHTML = originalText;
                  (propertyReportLink as HTMLElement).style.pointerEvents = 'auto';
                });
            } else {
              // Call handlePropertyReportClick directly instead of dispatching an event
              handlePropertyReportClick(property);
            }
          };
          
          addSafeEventListener(propertyReportLink, 'click', propertyReportLinkHandler);
        }
      }
    );
    
    // Clean up when info window closes
    const infoWindowCloseListener = addMapsEventListener(
      propertyInfoWindowRef.current, 
      'closeclick', 
      () => {
        removeMapsEventListener(infoWindowOpenListener);
        removeMapsEventListener(infoWindowCloseListener);
        cleanupAllEventListeners();
      }
    );
  }, [map, handleOwnerDetailsClick, handlePropertyReportClick, showOwnerDetails, showConfirmModal, showPropertyReportConfirmModal, propertyReportCost, ownerDetailsCost, toTitleCase, addMapsEventListener, removeMapsEventListener, cleanupAllEventListeners]);

  // Define the property marker click handler
  const handlePropertyMarkerClick = useCallback((property: any) => {
    // Skip this operation if any modal is open
    if (showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal) {
      return;
    }
    
    // Close any existing property info windows
    if (propertyInfoWindowRef.current) {
      propertyInfoWindowRef.current.close();
    }
    
    // Close any search info windows
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      setShowSearchInfoWindow(false);
    }
    
    // Clean up any existing event listeners
    cleanupAllEventListeners();
    
    // Check if the property exists and has a position
    if (!property) return;
    
    // Get the position from the property
    const position = {
      lat: parseFloat(property.latitude || property.lat || 0),
      lng: parseFloat(property.longitude || property.lng || 0)
    };
    
    // Show the property info window
    showPropertyInfoWindow(property, position);
  }, [showOwnerDetails, showConfirmModal, showPropertyReportConfirmModal, showPropertyInfoWindow, cleanupAllEventListeners, setShowSearchInfoWindow]);

  // Assign the handlePropertyMarkerClick function to the ref to break the circular reference
  useEffect(() => {
    markerClickHandlerRef.current = handlePropertyMarkerClick;
  }, [handlePropertyMarkerClick]);

  // Handle place selection from Google Places Autocomplete
  const handlePlaceChanged = useCallback((location: { lat: number; lng: number }) => {
    if (map && location) {
      // Update the map center and zoom
      map.panTo(location);
      map.setZoom(15);
      
      // Set the search location
      setSearchLocation(location);
      setShowCircle(false);
      setSearchRadius(null);
      
      // Show the info window with radius options
      showInfoWindow(location);
    }
  }, [map, showInfoWindow]);

  // Callback for handling polygon search
  const handlePolygonSearch = useCallback(async (polygon: google.maps.Polygon) => {
    if (!map || !polygon) return [];

    try {
      const polygonPath = polygon.getPath().getArray();
      const polygonCenter = calculatePolygonCenter(polygon);
      if (!polygonCenter) return [];
      
      const polygonDiameter = calculatePolygonDiameter(polygon);

      // Search properties within the circle that encompasses the polygon
      const allProperties: any[] = await searchPropertiesByRadius(polygonCenter, polygonDiameter * 1000); // Convert to meters

      // Now filter the properties to only include those inside the polygon
      const propertiesInPolygon = allProperties.filter(property => {
        const propertyPosition = new google.maps.LatLng(
          parseFloat(property.latitude || property.lat || 0),
          parseFloat(property.longitude || property.lng || 0)
        );
        return isPointInPolygon(propertyPosition, polygon);
      });

      if (propertiesInPolygon.length > 0) {
        // Create markers only for properties within the polygon
        const markers = createPropertyMarkers(propertiesInPolygon);
        setPropertyMarkers(markers);
        
        // Create legend items
        const items = createLegendItems(propertiesInPolygon, getPropertyTypeColor);
        setLegendItems(items);
        
        // Inform parent component about the properties found
        onPropertiesFound(propertiesInPolygon);
        
        toast.success(`Found ${propertiesInPolygon.length} properties within the polygon`);
        return propertiesInPolygon;
      } else {
        toast.info("No properties found within the polygon");
        // Clear any existing markers
        setPropertyMarkers([]);
        setLegendItems([]);
        onPropertiesFound([]);
        return [];
      }

      // Change to pan mode after search is completed
      setMapMode('pan');
    } catch (error) {
      toast.error("Error searching properties within the polygon");
      return [];
    }
  }, [map, searchPropertiesByRadius, getPropertyTypeColor, setPropertyMarkers, setLegendItems, setMapMode, onPropertiesFound]);

  // Add function to toggle maximized state
  const toggleMaximized = useCallback(() => {
    setIsMaximized(!isMaximized);
  }, [isMaximized]);

  // Add effect to initialize drawing manager when mapMode changes to 'polygon'
  useEffect(() => {
    if (!map || !isLoaded) return;

    // If we're in polygon mode, initialize the drawing manager
    if (mapMode === 'polygon') {
      // Clean up any existing drawing manager
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
      }

      // If there's an existing polygon, remove it
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }

      // Create a new drawing manager
      const manager = new google.maps.drawing.DrawingManager({
        drawingMode: google.maps.drawing.OverlayType.POLYGON,
        drawingControl: false, // We'll control it through our UI
        polygonOptions: {
          fillColor: '#92ab1c',
          fillOpacity: 0.3,
          strokeColor: '#92ab1c',
          strokeWeight: 2,
          clickable: true,
          editable: false,
          zIndex: 1
        }
      });

      // Add the manager to the map
      manager.setMap(map);
      drawingManagerRef.current = manager;
      setDrawingManager(manager);

      // Add event listener for polygon completion
      const polygonCompleteListener = google.maps.event.addListener(
        manager,
        'polygoncomplete',
        (polygon: google.maps.Polygon) => {
          // Reset the drawing mode
          manager.setDrawingMode(null);
          
          // Store the polygon ref
          polygonRef.current = polygon;
          
          // Set the polygon state
          setPolygon(polygon);

          // Store the polygon path
          const path = polygon.getPath().getArray();
          setPolygonPath(path);

          // Make the polygon non-editable after completion
          polygon.setEditable(false);
          
          // Set options to make the polygon more visible
          polygon.setOptions({
            fillColor: '#92ab1c',
            fillOpacity: 0.3,
            strokeColor: '#92ab1c',
            strokeWeight: 3,
            clickable: true
          });

          // Perform search using the polygon
          handlePolygonSearch(polygon).then(() => {
            // Change to pan mode after search is completed
            setMapMode('pan');
          });
        }
      );

      // Return cleanup function
      return () => {
        if (polygonCompleteListener) {
          google.maps.event.removeListener(polygonCompleteListener);
        }
        if (manager) {
          manager.setMap(null);
        }
      };
    } else {
      // Clean up drawing manager when not in polygon mode
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
        drawingManagerRef.current = null;
        setDrawingManager(null);
      }
      
      // Keep the polygon visible even when not in polygon mode
      // This ensures the polygon stays on the map after searching
    }
  }, [map, isLoaded, mapMode, handlePolygonSearch, setMapMode]);

  // Add effect to listen for clearPolygon event
  useEffect(() => {
    // Function to handle clearing the polygon
    const handleClearPolygon = () => {
      // Clear any existing polygon
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
        setPolygon(null);
      }
      
      // Reinitialize the drawing manager if in polygon mode
      if (mapMode === 'polygon' && map) {
        // Clear any existing drawing manager
        if (drawingManagerRef.current) {
          drawingManagerRef.current.setMap(null);
        }
        
        // Create a new drawing manager
        const manager = new google.maps.drawing.DrawingManager({
          drawingMode: google.maps.drawing.OverlayType.POLYGON,
          drawingControl: false,
          polygonOptions: {
            fillColor: '#92ab1c',
            fillOpacity: 0.3,
            strokeColor: '#92ab1c',
            strokeWeight: 2,
            clickable: true,
            editable: false,
            zIndex: 1
          }
        });
        
        // Add the manager to the map
        manager.setMap(map);
        drawingManagerRef.current = manager;
        setDrawingManager(manager);
        
        // Clear existing property markers
        setPropertyMarkers([]);
        setLegendItems([]);
        onPropertiesFound([]);
      }
    };
    
    // Add event listener
    document.addEventListener('clearPolygon', handleClearPolygon);
    
    // Cleanup function
    return () => {
      document.removeEventListener('clearPolygon', handleClearPolygon);
    };
  }, [map, mapMode, setPropertyMarkers, setLegendItems, onPropertiesFound]);

  // Render loading state
  if (!isLoaded) {
    return (
      <div className="map-search-container">
        <div className="map-search-loading-container">
          <div className="map-search-loading">
            <h3>Loading Google Maps</h3>
            <p>Please wait while we load the map...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (loadError) {
    return (
      <div className="map-search-container">
        <div className="map-search-loading-container">
          <div className="map-search-loading-error">
            <h3>Error loading Google Maps</h3>
            <p>{loadError.message}</p>
            <p>Please check your internet connection and try again. If the problem persists, please contact support.</p>
            <button 
              className="btn btn-primary mt-3" 
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // When applying the maximized class, ensure no additional styles interfere
  const mapSearchContainerClasses = isMaximized
    ? 'map-search-container map-search-container-maximized'
    : 'map-search-container';

  return (
    <div className={mapSearchContainerClasses} style={{ margin: 0, padding: 0 }}>
      {/* Add style tag for modal blocking styles */}
      <style>{modalBlockingStyles}</style>
      <SearchTabs
        map={map}
        mapMode={mapMode}
        setMapMode={setMapMode}
        handleUseCurrentLocation={handleUseCurrentLocation}
        onPlaceChanged={handlePlaceChanged}
        onPropertiesFound={onPropertiesFound}
        setIsSearching={setIsSearching}
        showPropertyInfoWindow={showPropertyInfoWindow}
        setPropertyMarkers={setPropertyMarkers}
        setLegendItems={setLegendItems}
        getPropertyTypeColor={getPropertyTypeColor}
        isMaximized={isMaximized}
        toggleMaximized={toggleMaximized}
      />
      <div 
        className="map-search-map-container" 
        style={{ padding: 0, margin: 0 }}
      >
        <GoogleMap
          mapContainerClassName={`map-container ${showOwnerDetails || showConfirmModal || showPropertyReportConfirmModal ? 'modal-open' : ''}`}
          mapContainerStyle={{ 
            height: '100%', 
            width: '100%', 
            margin: 0, 
            padding: 0,
            overflow: 'hidden' 
          }}
          center={center}
          zoom={zoom}
          options={{
            ...getGoogleMapOptions(mapMode, GOOGLE_MAPS_ID),
            fullscreenControl: false, // Disable the built-in fullscreen control
          }}
          onLoad={handleMapLoad}
          onClick={handleMapClick}
        >
          {/* Search circle */}
          {searchLocation && isLoaded && showCircle && searchRadius && (
            <Circle
              center={searchLocation}
              radius={searchRadius}
              options={searchCircleOptions}
            />
          )}
          
          {/* Property markers */}
          {propertyMarkers.map(marker => (
            <PropertyMarker
              key={marker.id}
              marker={marker}
              getSingleUnitMarkerOptions={getSingleUnitMarkerOptions}
              getMultiUnitMarkerStyle={getMultiUnitMarkerStyle}
              onMarkerClick={handlePropertyMarkerClick}
              toTitleCase={toTitleCase}
            />
          ))}
          
          {/* Info windows */}
          {searchLocation && infoWindowRef.current && showSearchInfoWindow && (
            <SearchInfoWindow
              location={searchLocation}
              infoWindow={infoWindowRef.current}
              map={map}
              onRadiusSelect={handleRadiusSelect}
            />
          )}
        </GoogleMap>
        
        {/* Map legend */}
        {legendItems.length > 0 && (
          <div className="map-search-map-legend">
            <div><strong>Property Types:</strong></div>
            {legendItems.map((item, index) => (
              <div key={index} style={{ marginTop: '5px' }}>
                <span 
                  style={{ 
                    backgroundColor: item.color, 
                    width: '12px', 
                    height: '12px', 
                    display: 'inline-block', 
                    borderRadius: '2px', 
                    marginRight: '5px' 
                  }}
                ></span>
                {item.type}
              </div>
            ))}
          </div>
        )}
        
        {/* Loading overlay */}
        {isSearching && (
          <div className="map-search-loading-container">
            <LoadingOverlay message="Searching properties" />
          </div>
        )}
      </div>
      
      {/* Confirmation Modal for Owner Details */}
      {showConfirmModal && (
        <ConfirmReportPurchaseModalPopup
          showModal={showConfirmModal}
          title="Confirm Owner Details Purchase"
          creditBalance={creditBalance}
          reportCost={ownerDetailsCost}
          closeModalPopup={() => setShowConfirmModal(false)}
          confirmModalPopup={confirmOwnerDetailsPurchase}
          openPurchaseCreditsModal={() => setShowPurchaseCreditsModal(true)}
          isProcessing={isProcessingOwnerDetails}
        />
      )}
      
      {/* Confirmation Modal for Property Report */}
      {showPropertyReportConfirmModal && (
        <ConfirmReportPurchaseModalPopup
          showModal={showPropertyReportConfirmModal}
          title="Confirm Property Report Purchase"
          creditBalance={creditBalance}
          reportCost={propertyReportCost}
          closeModalPopup={() => setShowPropertyReportConfirmModal(false)}
          confirmModalPopup={confirmPropertyReportPurchase}
          openPurchaseCreditsModal={() => setShowPurchaseCreditsModal(true)}
          isProcessing={isProcessingPropertyReport}
        />
      )}
      
      {/* Owner Details Modal */}
      {showOwnerDetails && (ownerDetailsData || selectedPropertyForOwnerDetails?.ownerDetails) && ReactDOM.createPortal(
        <div className="modal show" style={{
          display: 'block',
          zIndex: 20000
        }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" style={{
            maxWidth: '800px',
            margin: '30px auto',
            zIndex: 20001
          }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Owner Details</h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => setShowOwnerDetails(false)}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <ReportOwnerDetails 
                  propertyId={ownerDetailsPropertyId || selectedPropertyForOwnerDetails?.propertyId}
                  propertyType={ownerDetailsPropertyType || selectedPropertyForOwnerDetails?.propertyType}
                  streetAddress={ownerDetailsStreetAddress || formatPropertyAddress(selectedPropertyForOwnerDetails)}
                  ownerDetailsData={ownerDetailsData || { ownerDetails: selectedPropertyForOwnerDetails?.ownerDetails }}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Add display name for debugging
MapSearchComponent.displayName = 'MapSearchComponent';

// Export the wrapped component
export default forwardRef(MapSearchWrapper);
